"use client";

import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { ChartContainer, type ChartConfig } from "@/core/components/ui/chart";
import {
  Bar,
  BarChart,
  XAxis,
  YAxis,
  LabelList,
  ReferenceLine,
  Tooltip,
} from "recharts";
import type { Excess3MonthsResponse } from "../services/excess-3-months-service";

const MONTH_TRANSLATION_KEYS: Record<string, string> = {
  "01": "dashboard-month-january-short",
  "02": "dashboard-month-february-short",
  "03": "dashboard-month-march-short",
  "04": "dashboard-month-april-short",
  "05": "dashboard-month-may-short",
  "06": "dashboard-month-june-short",
  "07": "dashboard-month-july-short",
  "08": "dashboard-month-august-short",
  "09": "dashboard-month-september-short",
  "10": "dashboard-month-october-short",
  "11": "dashboard-month-november-short",
  "12": "dashboard-month-december-short",
};

const MONTH_TRANSLATION_KEYS_LONG: Record<string, string> = {
  "01": "dashboard-month-january",
  "02": "dashboard-month-february",
  "03": "dashboard-month-march",
  "04": "dashboard-month-april",
  "05": "dashboard-month-may",
  "06": "dashboard-month-june",
  "07": "dashboard-month-july",
  "08": "dashboard-month-august",
  "09": "dashboard-month-september",
  "10": "dashboard-month-october",
  "11": "dashboard-month-november",
  "12": "dashboard-month-december",
};

function formatMonth(monthStr: string, t: TFunction): string {
  const parts = monthStr.split("-");
  const key = MONTH_TRANSLATION_KEYS[parts[1] ?? ""];
  return key ? t(key) : (parts[1] ?? monthStr);
}

function formatMonthFull(monthStr: string, t: TFunction): string {
  const parts = monthStr.split("-");
  const year = parts[0] ?? "";
  const key = MONTH_TRANSLATION_KEYS_LONG[parts[1] ?? ""];
  const monthName = key ? t(key) : (parts[1] ?? monthStr);
  return `${monthName} ${year}`;
}

export type ChartDataItem = {
  month: string;
  monthLabel: string;
  monthFullLabel: string;
  used: number;
  unused: number;
  excess: number;
  percentLabel: number;
};

function buildChartData(
  consumptionMonths: Excess3MonthsResponse["consumption_percentage_months"],
  t: TFunction,
): ChartDataItem[] {
  return consumptionMonths.map((item) => {
    const pct = item.consumed_percentage;
    const used = pct <= 1 ? pct : 0;
    const unused = pct <= 1 ? 1 - pct : 0;
    const excess = pct > 1 ? pct : 0;
    return {
      month: item.month,
      monthLabel: formatMonth(item.month, t),
      monthFullLabel: formatMonthFull(item.month, t),
      used,
      unused,
      excess,
      percentLabel: Math.round(pct * 100),
    };
  });
}

const CHART_CONFIG = {
  used: {
    label: "exceeded-credits-modal-legend-used",
    color: "#24776F",
  },
  unused: {
    label: "exceeded-credits-modal-legend-contracted",
    color: "#E9F1F1",
  },
  excess: {
    label: "exceeded-credits-modal-legend-excess",
    color: "#F07575",
  },
} satisfies ChartConfig;

/** Darker colors for bar hover (activeBar). */
const CHART_CONFIG_HOVER = {
  used: "#164743",
  unused: "#d0e0de",
  excess: "#904646",
} as const;

export type ExceededCreditsCardModalChartProps = {
  consumptionMonths: Excess3MonthsResponse["consumption_percentage_months"];
  totalCredits?: number;
};

function ChartTooltipContent({
  payload,
  active,
  totalCredits,
  t,
}: {
  payload?: Array<{ payload?: ChartDataItem }>;
  active?: boolean;
  totalCredits?: number;
  t: TFunction;
}) {
  const data = active && payload?.length ? payload[0]?.payload : null;
  if (!data) return null;
  const contracted = totalCredits ?? 0;
  const usedCredits = Math.round((contracted * data.percentLabel) / 100);
  const isExcess = data.percentLabel > 100;
  const excessCredits = isExcess ? usedCredits - contracted : 0;

  return (
    <div className="rounded-[16px] overflow-hidden shadow-md w-max min-w-[180px] max-w-[350px] border border-border">
      {/* Header: month and year with grayLight-400 background */}
      <div className="bg-lightgray-400 px-[10px] py-[8px] text-center">
        <p className="font-normal text-darkgray-900 text-xs">
          {data.monthFullLabel}
        </p>
      </div>
      {/* Content: 2 or 3 column grid with equal lateral padding */}
      <div className="bg-white py-[16px] px-[8px]">
        <div className="flex flex-row place-items-center">
          {isExcess && (
            <div className="border-r border-lightgray-400 min-w-[110px] w-[110px] p-2">
              <div className="flex flex-col gap-1 text-left">
                <p className="text-xs text-graydark-700">
                  {t("exceeded-credits-tooltip-excess-pct", "Excedido em %")}
                </p>
                <p className="text-sm font-semibold text-foreground">
                  {data.percentLabel}%
                </p>
              </div>
            </div>
          )}
          <div className="border-r border-lightgray-400 min-w-[110px] w-[110px] p-2">
            <div className="flex flex-col gap-1 items-center">
              <p className="text-xs text-graydark-700">
                {t("exceeded-credits-tooltip-contracted", "Contratados")}
              </p>
              <p className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                <span className="size-2 rounded-full shrink-0 bg-info-500" />
                {contracted.toLocaleString("pt-BR")}
              </p>
            </div>
          </div>
          <div className="min-w-[110px] w-[110px] p-2">
            <div className="flex flex-col gap-1 items-center">
              <p className="text-xs text-graydark-700">
                {isExcess
                  ? t("exceeded-credits-tooltip-exceeded", "Excedidos")
                  : t("exceeded-credits-tooltip-used", "Usados")}
              </p>
              <p className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                <span
                  className={`size-2 rounded-full shrink-0 ${isExcess ? "bg-coral-500" : "bg-forest-500"}`}
                />
                {isExcess
                  ? excessCredits.toLocaleString("pt-BR")
                  : usedCredits.toLocaleString("pt-BR")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ExceededCreditsCardModalChart({
  consumptionMonths,
  totalCredits,
}: ExceededCreditsCardModalChartProps) {
  const { t } = useTranslation(["dashboard"]);
  const chartData = buildChartData(consumptionMonths, t);

  return (
    <div className="rounded-lg bg-white shadow-lg shadow-[#854d0e08]">
      <div className="border-b border-lightgray-400 p-3">
        <span className="text-sm font-normal text-darkgray-900">
          {t("exceeded-credits-modal-consumption-chart", "Consumo de créditos")}
        </span>
      </div>
      <div className="p-6">
        <ChartContainer
          config={CHART_CONFIG}
          className="min-h-[200px] w-full h-80"
        >
          <BarChart
            accessibilityLayer
            data={chartData}
            margin={{ top: 24, right: 10, left: 10, bottom: 30 }}
          >
            <Tooltip
              cursor={false}
              content={(props) => (
                <ChartTooltipContent
                  {...props}
                  totalCredits={totalCredits}
                  t={t}
                />
              )}
            />
            <XAxis
              dataKey="monthLabel"
              tick={{ fontSize: 11 }}
              tickLine={false}
            />
            <YAxis domain={[0, 1.5]} hide />
            <Bar
              dataKey="used"
              stackId="a"
              fill="var(--color-used)"
              radius={[0, 0, 0, 0]}
              isAnimationActive={false}
              activeBar={{ fill: CHART_CONFIG_HOVER.used }}
            />
            <Bar
              dataKey="unused"
              stackId="a"
              fill="var(--color-unused)"
              radius={[6, 6, 0, 0]}
              isAnimationActive={false}
              activeBar={{ fill: CHART_CONFIG_HOVER.unused }}
            />
            <Bar
              dataKey="excess"
              stackId="a"
              fill="var(--color-excess)"
              radius={[6, 6, 0, 0]}
              isAnimationActive={false}
              activeBar={{ fill: CHART_CONFIG_HOVER.excess }}
            >
              <LabelList
                dataKey="percentLabel"
                position="top"
                formatter={(v: number) => `${v}%`}
                className="text-[10px] fill-muted-foreground"
              />
            </Bar>
            <ReferenceLine
              y={1}
              strokeDasharray="4 4"
              stroke="#333"
              strokeOpacity={0.6}
            />
          </BarChart>
        </ChartContainer>
        <div className="flex justify-center gap-6 mt-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <div
              className="w-4 h-0 border-t border-dashed border-muted-foreground"
              style={{ minWidth: 16 }}
            />
            <span>
              {t(
                "exceeded-credits-modal-legend-contracted",
                "Créditos contratados",
              )}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-forest-500" />
            <span>
              {t("exceeded-credits-modal-legend-used", "Créditos usados")}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-coral-500" />
            <span>
              {t("exceeded-credits-modal-legend-excess", "Créditos excedentes")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
